import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { NAV_GROUPS, MOBILE_TABS } from "@/shared/lib/navItems";
import { hasMinRole } from "@/shared/auth/permissions";
import { selectUser, useAuth } from "@/shared/auth/store";
import { cn } from "@/shared/lib/cn";

/**
 * Everything the four tabs don't cover, as a bottom sheet.
 *
 * Reads the same NAV_GROUPS the desktop sidebar renders — including the RBAC
 * filter — so a page can never exist on one and be missing from the other.
 * The three routes already promoted to tabs are dropped here rather than
 * repeated: a user hunting for 경보 will use the tab, and a duplicate entry
 * just makes the sheet longer.
 */
export interface MoreSheetProps {
  open: boolean;
  onClose: () => void;
}

export function MoreSheet({ open, onClose }: MoreSheetProps) {
  const { t } = useTranslation();
  const role = useAuth(selectUser)?.role;

  // Esc closes, and the body must not scroll behind the sheet.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const groups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(
      (it) =>
        !(MOBILE_TABS as readonly string[]).includes(it.to) &&
        (!it.minRole || hasMinRole(role, it.minRole)),
    ),
  })).filter((g) => g.items.length > 0);

  if (!open) return null;

  return (
    <div className="lg:hidden fixed inset-0 z-[45]" role="dialog" aria-modal="true" aria-label={t("nav.tabMore")}>
      <button
        type="button"
        aria-label={t("common.close")}
        onClick={onClose}
        className="absolute inset-0 bg-black/40 animate-[fade-in_var(--motion-fast)_var(--ease-out-soft)_both]"
      />

      <div
        className={cn(
          "absolute inset-x-0 bottom-0 max-h-[82dvh] overflow-y-auto",
          "rounded-t-[var(--radius-2xl)] bg-[var(--bg-elevated)]",
          "border-t border-[var(--separator)] shadow-[var(--shadow-pop)]",
        )}
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}
      >
        <header className="sticky top-0 bg-[var(--bg-elevated)] flex items-center gap-2 px-4 pt-3 pb-2 border-b border-[var(--separator)]">
          <span aria-hidden className="absolute left-1/2 -translate-x-1/2 top-1.5 h-1 w-9 rounded-full bg-[var(--border-strong)]" />
          <h2 className="mt-1.5 text-[14px] font-semibold text-[var(--fg)]">{t("nav.tabMore")}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="mt-1.5 ml-auto h-9 w-9 grid place-items-center rounded-[var(--radius-md)] text-[var(--fg-muted)] active:bg-[var(--bg-active)]"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>

        {groups.map((g) => (
          <section key={g.titleKey || g.items[0]?.to} className="px-2 pt-3">
            {g.titleKey && (
              <h3 className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--fg-subtle)]">
                {t(g.titleKey)}
              </h3>
            )}
            <ul className="grid grid-cols-2 gap-1">
              {g.items.map((it) => (
                <li key={it.to}>
                  <NavLink
                    to={it.to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2.5 px-3 min-h-[48px] rounded-[var(--radius-md)]",
                        "text-[13px] active:bg-[var(--bg-active)]",
                        isActive
                          ? "bg-[var(--bg-subtle)] text-[var(--fg)] font-semibold"
                          : "text-[var(--fg-muted)]",
                      )
                    }
                  >
                    <it.icon
                      size={17}
                      strokeWidth={1.75}
                      className="shrink-0"
                      style={{ color: `var(${g.tone})` }}
                    />
                    <span className="truncate">{t(it.labelKey)}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
